import type { DashboardLabels, DashboardPeriod, RevenueChartPoint } from "./types";

/** Format a minor-unit (kuruş) amount as `₺24.850,00` style string. */
export function formatCurrencyMinor(
  minor: number,
  currency: "TRY" | "EUR" | "USD" = "TRY",
  locale = "tr-TR",
): string {
  const major = minor / 100;
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(major);
}

/** Compact currency for chart axes ("24,8K", "1,2M"). */
export function formatCurrencyCompact(minor: number, locale = "tr-TR"): string {
  const major = minor / 100;
  return new Intl.NumberFormat(locale, {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(major);
}

export function formatDateShort(iso: string, locale = "tr-TR"): string {
  const d = new Date(iso);
  return new Intl.DateTimeFormat(locale, { day: "2-digit", month: "short" }).format(d);
}

/** Initials from a person/branch name. Max 2 chars. */
export function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** Compute relative-freshness string. */
export function freshnessLabel(
  updatedAtIso: string,
  now: Date,
  labels: DashboardLabels["page"],
): { text: string; staleness: "fresh" | "stale" | "very-stale" } {
  const updated = new Date(updatedAtIso).getTime();
  const diffMs = now.getTime() - updated;
  const diffMin = Math.max(0, Math.round(diffMs / 60_000));
  let value: string;
  if (diffMin < 1) value = labels.updatedJustNow;
  else value = labels.updatedMinutesAgo.replace("{n}", String(diffMin));
  const text = labels.updatedAt.replace("{value}", value);
  let staleness: "fresh" | "stale" | "very-stale" = "fresh";
  if (diffMin >= 10) staleness = "very-stale";
  else if (diffMin >= 1) staleness = "stale";
  return { text, staleness };
}

/** Pick greeting subtitle by current local hour. */
export function greetingSubtitle(
  now: Date,
  labels: DashboardLabels["page"],
): string {
  const h = now.getHours();
  if (h < 12) return labels.welcomeSubtitleMorning;
  if (h < 18) return labels.welcomeSubtitleAfternoon;
  return labels.welcomeSubtitleEvening;
}

/** Periods rendered in the segmented control, ordered. */
export const DASHBOARD_PERIODS: DashboardPeriod[] = ["7d", "30d", "90d", "1y"];

/** Build a seeded revenue series for demos. */
export function buildRevenueSeries(period: DashboardPeriod, seed = 42): RevenueChartPoint[] {
  const days =
    period === "7d" ? 7 : period === "30d" ? 30 : period === "90d" ? 90 : 365;
  // simple LCG for deterministic noise
  let s = seed;
  const rand = () => {
    s = (s * 1664525 + 1013904223) % 4_294_967_296;
    return s / 4_294_967_296;
  };
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const points: RevenueChartPoint[] = [];
  // group long ranges by week to keep chart readable
  const stepDays = period === "1y" ? 7 : period === "90d" ? 3 : 1;
  for (let i = days - 1; i >= 0; i -= stepDays) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    const trend = (days - i) / days; // 0..1 upward
    const weekday = d.getDay();
    const weekendBoost = weekday === 0 || weekday === 6 ? 1.35 : 1;
    const noise = 0.85 + rand() * 0.4;
    const baseMajor = 1500 + trend * 1800; // TL
    const valueMinor = Math.round(baseMajor * weekendBoost * noise * 100);
    points.push({ date: d.toISOString().slice(0, 10), valueMinor });
  }
  return points;
}

/** Sum minor values for the series tooltip footer / KPIs. */
export function sumSeriesMinor(series: RevenueChartPoint[]): number {
  return series.reduce((acc, p) => acc + p.valueMinor, 0);
}

/** Star rating helper. */
export function clampScore(score: number): number {
  return Math.max(0, Math.min(5, Math.round(score)));
}
